<template>
  <div v-if="user" class="page lightmap">
    <navigation-header />
    <vertical-stack class="main">
      <prompt-reset-password
        v-if="state === ViewState.PromptResetPassword"
        @skip="updateStateForUser"
      />
      <div v-else>
        <user-header />

        <user-details v-model="accountDetailsModalVisible" @close="onHideAccountDetailsModal" />

        <!-- Existing User -->
        <vertical-stack v-if="state === ViewState.ExistingUser">
          <div class="paragraph-content">
            {{ $t('user_details.account_created') }} {{ accountCreatedTimestamp }} <br>
            {{ $t('user_details.last_sign_in') }} {{ lastSignInTimestamp }}
          </div>
          <a href="" class="paragraph-content" @click.prevent="onShowAccountDetailsModal">{{
            t('links.see_more_account_details') }}</a>
        </vertical-stack>

        <!-- New User -->
        <vertical-stack v-else>
          <paragraph-content keypath="home.account_created" />

          <a href="" class="paragraph-content" @click.prevent="onShowAccountDetailsModal">{{
            t('links.view_my_account') }}</a>

          <large-button v-if="continueToProduct" class="button-container" @click.prevent="onContinueToAppClicked()">
            {{ continueToAppText }}
          </large-button>
        </vertical-stack>

        <vertical-stack class="buttons">
          <small-button
            v-if="state === ViewState.ExistingUser && (testState || hasPasswordProvider())"
            @click.prevent="redirectToResetPassword"
          >
            {{ $t('common.button.reset_password') }}
          </small-button>
          <small-button :loading="isLoading" variant="critical" @click.prevent="onSignOut()">
            {{ $t('common.button.sign_out') }}
          </small-button>
        </vertical-stack>

        <product-links />
      </div>
    </vertical-stack>
    <accounts-footer />
  </div>
</template>

<script setup>
import { watch, computed, ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useI18n as usePathfinderI18n } from 'ihunter-pathfinder-components';

import {
  AuthState, useAuthentication, useProduct, useLoadingState, useAnalytics,
  AvatarIcon, LargeButton, VerticalStack, HorizontalStack,
  StoreBadge, GooglePlayBadge, AppleStoreBadge, WebappBadge,
  UserDetails, SmallButton
} from 'ihunter-pathfinder-components';

import { useRouteManager } from '../composables/useRouteManager';
import NavigationHeader from '../components/NavigationHeader.vue';
import PromptResetPassword from '../components/PromptResetPassword.vue';
import UserHeader from '../components/UserHeader.vue';
import ProductLinks from '../components/ProductLinks.vue';
import AccountsFooter from '../components/AccountsFooter.vue';
import { useTestState } from '../composables/useTestState';

import UserProfile from '../components/UserProfile.vue';
import ParagraphContent from '../components/ParagraphContent.vue';

const { user, signOut, hasPasswordProvider } = useAuthentication();
const { product, continueToProduct } = useProduct();
const { isLoading, doLoadingOperation } = useLoadingState();
const { redirectToLogin, redirectToLoginWithAuthState, redirectToResetPassword } = useRouteManager();
const { initializeTestStateFromUrl, TestStateUser, testState } = useTestState();
const { t, te } = useI18n();
const { logScreenView } = useAnalytics();

const router = useRouter();
const accountDetailsModalVisible = ref(false);

const ViewState = {
  ExistingUser: 'existing_user',
  NewUser: 'new_user',
  PromptResetPassword: 'prompt_reset_password'
};
Object.freeze(ViewState);

const state = ref(ViewState.ExistingUser);

const { getShortDateFromTimestamp } = usePathfinderI18n();

const accountCreatedTimestamp = computed(() => getShortDateFromTimestamp(user.value?.metadata?.createdAt));

const lastSignInTimestamp = computed(() => getShortDateFromTimestamp(user.value?.metadata?.lastLoginAt));

const continueToAppText = computed(() => {
  const app = product.value.name;
  const key = (app === 'store') ? 'home.continue_to_the_app' : 'home.continue_to_app';
  return t(key, { app: t(`product.${app}.title`) });
});

onMounted(async () => {
  console.log(`Initialize HomeView`);
  initializeTestStateFromUrl(ViewState);
  if (testState.value) {
    setViewState(testState.value);
    user.value = user.value || TestStateUser;
    user.value.needsPasswordReset = (testState.value === ViewState.PromptResetPassword);
  } else {
    updateStateForUser();
  }
});

watch(user, async (user) => {
  if (!testState.value) {
    updateStateForUser();
  }
});

const updateStateForUser = () => {
  console.log(`HomeView user needsPasswordReset ${user.value?.needsPasswordReset}`);
  if (!user.value) {
    redirectToLogin();
  } else if (user.value.needsPasswordReset && state.value !== ViewState.PromptResetPassword) {
    setViewState(ViewState.PromptResetPassword);
  } else {
    setViewState(user.isNewUser ? ViewState.NewUser : ViewState.ExistingUser);
  }
}

const setViewState = (viewState) => {
  console.log(`Setting view state: ${viewState}`);
  logScreenView(viewState);
  state.value = viewState;
}

const onContinueToAppClicked = () => {
  if (continueToProduct.value && product.value.url) {
    console.log(`Redirecting to ${product.value.name}`);
    window.location = product.value.url;
  }
}

const onShowAccountDetailsModal = async () => {
  accountDetailsModalVisible.value = true;
};

const onHideAccountDetailsModal = async () => {
  accountDetailsModalVisible.value = false;
};

const onSignOut = async () => {
  console.log('onSignOut');
  await doLoadingOperation(signOut);
}

</script>

<style lang="scss" scoped>
@use 'ihunter-pathfinder/styles' as Pathfinder;
@use '../styles/Accounts' as Accounts;

.page {
  @include Accounts.Default;
  @include Accounts.Container;

  .main.vertical-stack {
    gap: 0;
  }

  .buttons {
    gap: Pathfinder.$space-150;
    margin-top: Pathfinder.$space-500;
    margin-bottom: Pathfinder.$space-500;
  }

  @include Pathfinder.below(min) {
    .buttons {
      margin-top: Pathfinder.$space-400;
      margin-bottom: Pathfinder.$space-400;
    }
  }
}
</style>
